<script setup lang="ts">
/**
 * MaterialSection —— `.scu-widget` 壳 + 附件列表（2026-06-25 R1/R6）。
 *
 * 模板无一一对应区，复用 `.scu-widget` + `.scu-grid-item` 视觉语言：
 * SectionCard 提供 widget 壳，每个附件渲染为 scu-grid-item 风格卡片
 * （bg / border / hover 上移），内含 fileName / `<RichText>` 描述 /
 * 元信息 / 下载按钮。下载链接直接消费后端完整 `resourceUrl` + `download`
 * 属性（前台 ADR-0007：不拼接 URL）。
 */
import { computed } from 'vue';

import SectionCard from './SectionCard.vue';
import RichTextViewer from '@/components/RichTextViewer.vue';
import type { SectionComponentProps } from './section-props';
import { copy } from '@/i18n/zh-CN';
import { formatBytes, hasText, sectionMaterials } from './section-presenters';

const props = defineProps<SectionComponentProps>();
const materials = computed(() => sectionMaterials(props.section));
</script>

<template>
  <SectionCard
    :id="props.id"
    type="material"
    :title="props.section.title"
    :summary="props.section.summary"
    :show-draft-badge="props.showDraftBadge"
  >
    <ul v-if="materials.length > 0" :class="$style.list">
      <li
        v-for="material in materials"
        :key="material.id"
        :class="$style.item"
      >
        <div :class="$style.info">
          <h3 :class="$style.name">{{ material.fileName }}</h3>
          <div v-if="hasText(material.description)" :class="$style.description">
            <RichTextViewer :html="material.description" as="div" />
          </div>
          <p :class="$style.meta">
            <span>{{ hasText(material.contentType) ? material.contentType : copy.sections.material.typeUnknown }}</span>
            <span>{{ formatBytes(material.sizeBytes) || copy.sections.material.sizeUnknown }}</span>
          </p>
        </div>
        <a
          v-if="hasText(material.resourceUrl)"
          :class="$style.download"
          :href="material.resourceUrl"
          :download="material.fileName"
        >
          {{ copy.buttons.download }}
        </a>
        <span v-else :class="$style.unavailable">
          {{ copy.fallback.asset.materialUnavailable }}
        </span>
      </li>
    </ul>
    <p v-else :class="$style.empty">{{ copy.sections.empty }}</p>
  </SectionCard>
</template>

<style module>
.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  transition:
    box-shadow var(--template-hover-duration) ease,
    transform var(--template-hover-duration) ease,
    border-color var(--template-hover-duration) ease;
}

.item:hover {
  border-color: var(--color-brand-primary);
  box-shadow: var(--shadow-card);
  transform: translateY(var(--template-hover-lift));
}

.info {
  min-width: 0;
}

.name {
  margin: 0;
  color: var(--color-brand-primary);
  font-size: var(--font-size-md);
}

.description {
  margin: var(--space-1) 0 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  margin: var(--space-1) 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.download,
.unavailable {
  flex: 0 0 auto;
  border-radius: var(--radius-sm);
  padding: var(--space-1) var(--space-2);
  font-size: var(--font-size-sm);
}

.download {
  background: var(--color-brand-primary);
  color: var(--color-bg-primary);
}

.download:hover {
  background: var(--color-brand-accent);
  color: var(--color-bg-primary);
}

.unavailable,
.empty {
  color: var(--color-text-muted);
}

.empty {
  margin: 0;
  font-size: var(--font-size-sm);
}
</style>
